export type IntakeImportPatch = {
  path: string;
  value: string;
  reason?: string;
};

export type IntakeImportResult = {
  summary?: string;
  patches: IntakeImportPatch[];
};

export const INTAKE_IMPORT_MAX_REPEATER_INDEX = 29;

const importableScalarPaths = new Set([
  "company.storeNameKo",
  "company.storeNameEn",
  "company.storeNameJa",
  "company.storeNameZh",
  "company.address",
  "company.kakaoMapsUrl",
  "company.kakaoCategory",
  "company.googleMapsUrl",
  "company.websiteUrl",
  "company.instagramUrl",
  "company.contentReferenceUrl",
  "reservation.bookableStartOffsetDays",
  "reservation.bookableWindowMonths",
  "reservation.businessHours",
  "reservation.reservationHours",
  "reservation.reservationUnit",
  "reservation.confirmationMode",
  "reservation.communicationChannel",
  "contract.contractType",
  "contract.upsellCommissionRate",
  "contract.depositPolicyMemo",
  "contract.settlementMemo",
  "content.catchphrase",
  "content.sellingPointTags",
  "content.touristConvenienceOptions",
  "content.primarySellingPoint",
  "content.targetCustomers",
  "content.targetCustomerConcernDetail",
  "content.recommendationReason",
  "content.creatripExclusiveBenefitStatus",
  "content.creatripExclusiveBenefit",
  "content.reviewBenefit",
  "content.clinicStrengths",
  "content.serviceEnvironment",
  "content.referenceLinks",
  "content.googleReviewIntegration",
  "content.googleBookingIntegration",
  "language.availableLanguages",
  "language.cautionLanguages",
  "policy.cancellationRefundPolicy",
  "policy.visitFlow",
  "policy.bookingQuestionChecklist",
  "policy.bookingQuestions",
  "policy.otherPrecautions",
  "policy.consultationFeePolicy",
  "policy.consultationFeeAmount",
  "policy.minorPolicy",
  "policy.passportNeeded",
  "policy.healthRestrictions",
]);

const importableRepeaterFields: Record<string, ReadonlySet<string>> = {
  products: new Set([
    "optionName",
    "listPrice",
    "salePrice",
    "depositOrSettlementPrice",
    "freeReservationLabel",
    "depositAmount",
    "settlementPrice",
    "maxQuantityPerSlot",
    "duration",
    "includedItems",
    "excludedItems",
    "recommendationReason",
    "detailDescription",
    "effectDuration",
    "recommendedTargets",
    "productPrecautions",
  ]),
  doctors: new Set(["name", "role", "profileText", "referenceUrl"]),
  procedures: new Set(["treatmentName", "description", "duration", "effectDuration"]),
};

const sensitivePathSegment = /password|passwd|secret|credential|token|apikey|accesskey|privatekey|authorization|cookie|notificationemails|notificationphones|adminloginid/i;
const repeaterPathPattern = /^(products|doctors|procedures)\.(0|[1-9]\d*)\.([A-Za-z][A-Za-z0-9]*)$/;

export function isImportableIntakeScalarPath(path: string): boolean {
  return importableScalarPaths.has(path);
}

export function isImportableIntakePatchPath(path: string): boolean {
  return isImportableIntakeScalarPath(path) || getIntakeImportRepeaterPath(path) !== null;
}

export function isSensitiveIntakeImportPath(path: string): boolean {
  return path.split(/[.\[\]]/).some((segment) => sensitivePathSegment.test(segment.replace(/[_-]/g, "")));
}

export function getIntakeImportRepeaterPath(path: string): { repeaterPath: string; index: number; fieldPath: string } | null {
  const match = path.match(repeaterPathPattern);
  if (!match) return null;
  const [, repeaterPath, indexText, fieldPath] = match;
  if (!importableRepeaterFields[repeaterPath]?.has(fieldPath) || Number(indexText) > INTAKE_IMPORT_MAX_REPEATER_INDEX) return null;
  return { repeaterPath, index: Number(indexText), fieldPath };
}
